import axios, { AxiosInstance } from 'axios';
import {
  State,
  StateFormData,
  StateSearchParams,
  StateApiResponse,
  StateCreateResponse,
  StateUpdateResponse,
  StateDeleteResponse,
  StateExportResponse,
  StateDropdownOption,
  Country
} from '../types/state';

export class StateService {
  private axios: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axios = axiosInstance;
  }

  /**
   * Get all states with pagination, filtering, and search
   */
  async getStates(params?: StateSearchParams): Promise<StateApiResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.search) {
      searchParams.append('search', params.search);
    }
    
    if (params?.page) {
      searchParams.append('page', params.page.toString());
    }
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    
    if (params?.sortBy) {
      searchParams.append('sortBy', params.sortBy);
    }
    
    if (params?.sortOrder) {
      searchParams.append('sortOrder', params.sortOrder);
    }

    // Add filters
    if (params?.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.append(key, value);
        }
      });
    }

    const response = await this.axios.get(`/api/master-data/state?${searchParams.toString()}`);
    return response.data;
  }

  /**
   * Get state by ID
   */
  async getStateById(id: string): Promise<{ success: boolean; data: State; message?: string }> {
    const response = await this.axios.get(`/api/master-data/state/${id}`);
    return response.data;
  }

  /**
   * Get state by code
   */
  async getStateByCode(code: string): Promise<{ success: boolean; data: State; message?: string }> {
    const response = await this.axios.get(`/api/master-data/state/code/${code}`);
    return response.data;
  }

  /**
   * Create new state
   */
  async createState(data: StateFormData): Promise<StateCreateResponse> {
    const response = await this.axios.post('/api/master-data/state', data);
    return response.data;
  }

  /**
   * Update state
   */
  async updateState(id: string, data: Partial<StateFormData>): Promise<StateUpdateResponse> {
    const response = await this.axios.put(`/api/master-data/state/${id}`, data);
    return response.data;
  }

  /**
   * Delete state (soft delete)
   */
  async deleteState(id: string, soft: boolean = true): Promise<StateDeleteResponse> {
    const params = soft ? '?soft=true' : '?soft=false';
    const response = await this.axios.delete(`/api/master-data/state/${id}${params}`);
    return response.data;
  }

  /**
   * Bulk create states
   */
  async bulkCreateStates(states: StateFormData[]): Promise<{ success: boolean; data: State[]; message?: string }> {
    const response = await this.axios.post('/api/master-data/state/bulk', { records: states });
    return response.data;
  }

  /**
   * Export states
   */
  async exportStates(filters?: StateSearchParams['filters']): Promise<StateExportResponse> {
    const searchParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.append(key, value);
        }
      });
    }

    const response = await this.axios.get(`/api/master-data/state/export?${searchParams.toString()}`);
    return response.data;
  }

  /**
   * Get states for dropdown
   */
  async getStatesForDropdown(search?: string): Promise<{ success: boolean; data: StateDropdownOption[]; message?: string }> {
    const searchParams = new URLSearchParams();
    if (search) {
      searchParams.append('search', search);
    }

    const response = await this.axios.get(`/api/master-data/state/dropdown?${searchParams.toString()}`);
    return response.data;
  }

  /**
   * Get states by country for dropdown
   */
  async getStatesByCountry(countryId: string): Promise<{ success: boolean; data: StateDropdownOption[]; message?: string }> {
    const response = await this.axios.get(`/api/master-data/state?country=${countryId}&status=Active&limit=100&sortBy=name&sortOrder=asc`);
    
    if (response.data.success) {
      const states = response.data.data.map((state: State) => ({
        value: state._id,
        label: state.name,
        code: state.code,
        portCode: state.portCode,
        gstStateCode: state.gstStateCode,
        country: typeof state.country === 'string' ? state.country : state.country._id
      }));
      
      return {
        success: true,
        data: states
      };
    }
    
    return response.data;
  }

  /**
   * Get countries for dropdown (needed for state form)
   */
  async getCountriesForDropdown(): Promise<{ success: boolean; data: Country[]; message?: string }> {
    const response = await this.axios.get('/api/master-data/country/dropdown');
    return response.data;
  }

  /**
   * Get state statistics
   */
  async getStateStats(): Promise<{
    success: boolean;
    data: {
      total: number;
      active: number;
      inactive: number;
    };
    message?: string;
  }> {
    const response = await this.axios.get('/api/master-data/state/stats');
    return response.data;
  }

  /**
   * Validate state code uniqueness
   */
  async validateStateCode(code: string, excludeId?: string): Promise<{ isUnique: boolean; message?: string }> {
    try {
      await this.getStateByCode(code);
      // If we get here, the code exists
      return { isUnique: false, message: 'State code already exists' };
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Code doesn't exist, so it's unique
        return { isUnique: true };
      }
      throw error;
    }
  }

  /**
   * Validate GST state code uniqueness
   */
  async validateGstStateCode(gstStateCode: string, excludeId?: string): Promise<{ isUnique: boolean; message?: string }> {
    try {
      const response = await this.getStates({
        filters: { status: 'Active' },
        limit: 1000 // Get all to check GST code
      });
      
      if (response.success) {
        const existingState = response.data.find(state => 
          state.gstStateCode === gstStateCode && 
          (!excludeId || state._id !== excludeId)
        );
        
        if (existingState) {
          return { isUnique: false, message: 'GST State code already exists' };
        }
      }
      
      return { isUnique: true };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Validate port code uniqueness
   */
  async validatePortCode(portCode: string, excludeId?: string): Promise<{ isUnique: boolean; message?: string }> {
    try {
      const response = await this.getStates({
        filters: { status: 'Active' },
        limit: 1000 // Get all to check port code
      });
      
      if (response.success) {
        const existingState = response.data.find(state => 
          state.portCode === portCode && 
          (!excludeId || state._id !== excludeId)
        );
        
        if (existingState) {
          return { isUnique: false, message: 'Port code already exists' };
        }
      }
      
      return { isUnique: true };
    } catch (error) {
      throw error;
    }
  }
}

// Create a default instance
let stateServiceInstance: StateService | null = null;

export const getStateService = (axiosInstance?: AxiosInstance): StateService => {
  if (!stateServiceInstance && axiosInstance) {
    stateServiceInstance = new StateService(axiosInstance);
  }
  return stateServiceInstance!;
};

export default StateService;
